/**
 * JS-side equivalent of the desktop's ``afri_sync.sync.run_pull`` —
 * fetches a project's tasks + per-task annotations + drafts from
 * the cloud and stashes them in IndexedDB so the Capacitor mobile
 * bundle can render LSF when offline.
 *
 * Hits the same endpoints the desktop sync engine uses
 * (``/api/projects/<id>/``, ``/api/projects/<id>/tasks/``,
 * ``/api/tasks/<id>/annotations``, ``/api/tasks/<id>/drafts``) so
 * the cloud doesn't need a new endpoint and idempotency / conflict
 * semantics carry over from the desktop story.
 *
 * Step 5d.1 + 5d.2 of the mobile pivot. Step 5d.3 (read-side
 * fallback in ``offline-fetch.ts``) is the next session — without
 * it, downloading is a write to IDB but nothing reads from there
 * yet.
 */
import {
  putRecord,
  getAllRecords,
  deleteRecord,
  STORE_PROJECTS,
  STORE_TASKS,
  STORE_ANNOTATIONS,
  STORE_DRAFTS,
  STORE_API_CACHE,
  STORE_URL_CONTENT,
} from "./offline-outbox";
import { authHeaders, apiUrl } from "./jwt-storage";
// Network-only fetch: bypasses the offline wrapper so a download can
// never be "served" from the very IDB mirror it is trying to fill.
import { networkFetch } from "./offline-fetch";

export type PullProgress = {
  /** Project we're pulling. */
  projectId: number;
  /** "metadata" → "tasks" → "annotations" → "drafts" → "done" */
  phase: "metadata" | "tasks" | "annotations" | "drafts" | "done";
  /** Fetched-so-far / total (when known). */
  fetched: number;
  total: number | null;
};

const TASK_PAGE_SIZE = 100;

/** Discriminator for user-initiated cancellation. Distinguishes from
 *  the generic network AbortError so ``downloadProjectForOffline`` can
 *  fold cleanly into a "cancelled with N tasks so far" toast instead
 *  of the "download failed: <exception>" error path. */
export class DownloadCancelled extends Error {
  constructor() {
    super("Download cancelled");
    this.name = "DownloadCancelled";
  }
}

/**
 * Persist-and-resume checkpoint for the mobile pull. Stored in
 * localStorage as a compact JSON blob keyed by project id — small
 * enough (~6 chars per id × ~500 ids = ~3KB) that we don't need IDB.
 *
 * A checkpoint records which task IDs have already had their
 * annotations + drafts fetched. On the next download attempt we skip
 * those tasks — an annotator whose phone screen locked mid-download,
 * or whose WebView crashed, doesn't have to re-fetch the 240 tasks
 * that already made it into IDB before the failure.
 *
 * Checkpoint is cleared on successful completion. On cancel it stays
 * so a re-tap can resume. On errors it also stays for the same
 * reason.
 */
const CHECKPOINT_KEY = (projectId: number) => `afri:mobile:project:${projectId}:progress`;

type Checkpoint = { completedTaskIds: number[]; updatedAt: number };

const readCheckpoint = (projectId: number): Set<number> => {
  try {
    if (typeof window === "undefined") return new Set();
    const raw = window.localStorage.getItem(CHECKPOINT_KEY(projectId));
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as Checkpoint;
    return new Set(Array.isArray(parsed?.completedTaskIds) ? parsed.completedTaskIds : []);
  } catch (_) {
    return new Set();
  }
};

const writeCheckpoint = (projectId: number, completed: Set<number>): void => {
  try {
    if (typeof window === "undefined") return;
    const payload: Checkpoint = { completedTaskIds: Array.from(completed), updatedAt: Date.now() };
    window.localStorage.setItem(CHECKPOINT_KEY(projectId), JSON.stringify(payload));
  } catch (_) {
    /* localStorage quota / disabled — pull still works, just no resume next time */
  }
};

const clearCheckpoint = (projectId: number): void => {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.removeItem(CHECKPOINT_KEY(projectId));
  } catch (_) {
    /* ignore */
  }
};

/** Public inspector — returns the size of the persisted resume set
 *  so the UI can render "Resume download (240 / 500)" instead of a
 *  generic "download" affordance when a checkpoint exists. */
export const getCheckpointSize = (projectId: number): number => readCheckpoint(projectId).size;

/** Throw ``DownloadCancelled`` if the caller's AbortSignal has fired.
 *  Called at every fetch boundary + between paginated pages + between
 *  per-task loop iterations so a mid-download cancel unwinds within
 *  ~1 request. */
const bail = (signal?: AbortSignal) => {
  if (signal?.aborted) throw new DownloadCancelled();
};

const fetchJson = async <T>(url: string, signal?: AbortSignal): Promise<T> => {
  bail(signal);
  // Mobile bundle authenticates via JWT in localStorage, NOT a
  // session cookie. ``credentials: same-origin`` alone sends only
  // cookies, so without ``authHeaders()`` (which injects
  // ``Authorization: Bearer <jwt>``) the request reaches Django as
  // AnonymousUser and the cloud rejects every detail endpoint.
  // Caller layer (ApiProvider) does this for app-level requests;
  // ``offline-sync.ts`` uses bare fetch and has to add it itself.
  const r = await networkFetch(apiUrl(url), {
    credentials: "same-origin",
    headers: { ...authHeaders() },
    signal,
  });
  if (!r.ok) {
    // Translate the most common server responses to actionable
    // messages — the toast that surfaces the throw goes to
    // annotators, not Django backend devs, so a raw HTTP code
    // doesn't help them.
    if (r.status === 404) {
      throw new Error(
        `Project not available (${url}). The project may have been deleted, or your account isn't a member of it.`,
      );
    }
    if (r.status === 401 || r.status === 403) {
      throw new Error("Not authorised to download this project. Try signing out and back in.");
    }
    throw new Error(`${url} → HTTP ${r.status}`);
  }
  return (await r.json()) as T;
};

/**
 * Iterate a paginated DRF endpoint. Returns all results across
 * pages. ``page`` query param + ``page_size`` matches what desktop's
 * ``cloud_get`` does. Stops when the cloud returns an empty page,
 * a non-paginated response, or an HTTP error (the latter throws).
 */
const iteratePaginated = async <T>(path: string, signal?: AbortSignal): Promise<T[]> => {
  const out: T[] = [];
  for (let page = 1; ; page += 1) {
    bail(signal);
    const r = await networkFetch(
      apiUrl(`${path}${path.includes("?") ? "&" : "?"}page=${page}&page_size=${TASK_PAGE_SIZE}`),
      {
        credentials: "same-origin",
        headers: { ...authHeaders() },
        signal,
      },
    );
    if (!r.ok) {
      if (r.status === 404 && page > 1) break; // ran past last page
      throw new Error(`${path} page=${page} → HTTP ${r.status}`);
    }
    const body = await r.json();
    let rows: T[];
    if (Array.isArray(body)) rows = body;
    else if (body && Array.isArray(body.results)) rows = body.results as T[];
    else rows = [];
    if (rows.length === 0) break;
    out.push(...rows);
    if (rows.length < TASK_PAGE_SIZE) break;
  }
  return out;
};

type Task = { id: number; project: number; data?: unknown };
type Annotation = { id: number; task: number };
type Draft = { id: number; task: number };

/** Walk a task's ``data`` payload and yield every value that looks
 *  like a URL — used to pre-warm the service worker's media cache
 *  so downloaded tasks render offline.
 *
 *  Heuristic only: LSF task data is an opaque dict keyed by labeling
 *  config variables (``$image``, ``$audio``, etc.), so we don't know
 *  WHICH keys carry media. We include any value that:
 *    1. Starts with ``http(s)://`` — absolute URL, anywhere.
 *    2. Starts with a known media path prefix (``/data/``,
 *       ``/static/samples/``, ``/static/uploads/``). ``/static/`` is
 *       broader than just ``/data/`` because cloud demos + sample
 *       projects ship media under ``/static/samples/`` (e.g. the
 *       reference ``game.wav`` audio task) — without these, prewarm
 *       missed every sample file and offline playback failed.
 *    3. Looks like a media file by extension (``.mp3``, ``.csv``,
 *       etc.) — catches cases where authors stash media URLs at
 *       unusual paths.
 *
 *  Worst case the SW skips a non-media fetch via its
 *  ``isMediaRequest`` filter, so over-collecting is cheap. */
const MEDIA_PATH_PREFIXES = ["/data/", "/static/samples/", "/static/uploads/"];
const MEDIA_EXTENSION_RE =
  /\.(?:jpe?g|png|gif|webp|svg|bmp|mp3|wav|m4a|aac|ogg|opus|flac|mp4|webm|mov|avi|pdf|json|csv|tsv|txt|xml|zip)(?:\?|$|#)/i;
const looksLikeMediaUrl = (v: string): boolean => {
  if (/^https?:\/\//i.test(v)) return true;
  if (MEDIA_PATH_PREFIXES.some((p) => v.startsWith(p))) return true;
  if (v.startsWith("/") && MEDIA_EXTENSION_RE.test(v)) return true;
  return false;
};
export const extractMediaUrls = (data: unknown): string[] => {
  const urls: string[] = [];
  const visit = (v: unknown): void => {
    if (typeof v === "string") {
      if (looksLikeMediaUrl(v)) urls.push(v);
    } else if (Array.isArray(v)) {
      v.forEach(visit);
    } else if (v && typeof v === "object") {
      Object.values(v as Record<string, unknown>).forEach(visit);
    }
  };
  visit(data);
  return urls;
};

/** Pre-fetch media URLs so the service worker caches them.
 *
 *  Tries ``mode: 'cors'`` first — that produces a fully-readable
 *  response the SW can cache and JS-side XHR can later replay
 *  (status 200, full body). For URLs whose upstream doesn't send
 *  CORS headers (legacy S3 presign with no CORS config) the cors
 *  fetch fails; fall back to ``mode: 'no-cors'`` so AT LEAST
 *  ``<audio>``/``<img>`` tags can play the cached response (those
 *  bypass the JS read restriction). The SW skips caching opaque
 *  responses by default, so the no-cors fallback only helps for
 *  same-origin or properly-CORS'd upstreams.
 *
 *  Cache-key alignment is critical: the URL we fetch here MUST
 *  match the URL LSF later requests, byte-for-byte, or the SW's
 *  cache lookup misses. Task data typically holds relative paths
 *  like ``/data/upload/123/foo.mp3``; on Capacitor those resolve
 *  against the WebView origin (``http://localhost/``) at prewarm
 *  time, but LSF rewrites them to absolute via APP_SETTINGS.hostname
 *  (``http://localhost:8080/``) at render time. Two different cache
 *  keys → eternal miss. ``apiUrl()`` does the same prefix that
 *  LSF's TaskStore.dataObj rewrite does, so both paths land on the
 *  same cache entry.
 *
 *  Failures swallowed — a single broken URL shouldn't kill the
 *  rest of the pull. */
const prewarmMedia = async (urls: string[], signal?: AbortSignal): Promise<void> => {
  const seen = new Set<string>();
  // eslint-disable-next-line no-console
  console.info("[offline-sync] prewarm starting urls=", urls.length);
  let okCount = 0;
  let failCount = 0;
  // Pool of 4: media files are bandwidth-bound but the old serial
  // loop paid full RTT per file; small parallelism keeps the pipe
  // full without saturating a phone connection.
  const unique: string[] = [];
  for (const raw of urls) {
    const u0 = apiUrl(raw);
    if (!seen.has(u0)) {
      seen.add(u0);
      unique.push(raw);
    }
  }
  let cursor = 0;
  const one = async (raw: string): Promise<void> => {
    bail(signal);
    // Canonicalise to the cloud-prefixed absolute URL — same shape
    // LSF will request when rendering. ``apiUrl`` is a no-op for
    // URLs that already have a scheme.
    const u = apiUrl(raw);
    try {
      // ``cache: 'reload'`` skips the HTTP cache so the SW always
      // intercepts and stashes the response under its own
      // afri-media cache. ``mode: 'cors'`` first so we get a
      // readable body — needed for the IDB stash below; opaque
      // fallback only helps <audio>/<img>, not LSF text/csv tags.
      //
      // Don't send the Authorization header for /static/ paths.
      // Static files are served by Django's ``serve`` view which
      // doesn't gate on auth, AND adding the Authorization header
      // makes the request "non-simple" → triggers a CORS preflight
      // (OPTIONS) that may be rejected for static endpoints in some
      // setups. Conservative: only send the JWT for paths we KNOW
      // are auth-gated (/data/upload/, /api/).
      const isAuthGated = u.includes("/data/upload/") || u.includes("/data/local-files/") || u.includes("/api/");
      const headers = isAuthGated ? { ...authHeaders() } : undefined;
      const r = await networkFetch(u, { cache: "reload", mode: "cors", headers });
      if (r && r.ok) {
        // eslint-disable-next-line no-console
        console.info("[offline-sync] prewarm ok", u, "type=", r.type, "ct=", r.headers.get("content-type"));
        okCount += 1;
        try {
          const blob = await r.clone().blob();
          await putRecord(STORE_URL_CONTENT, {
            key: u,
            body: blob,
            contentType: r.headers.get("content-type") || blob.type || "application/octet-stream",
            savedAt: Date.now(),
          });
        } catch (_) {
          /* swallow — IDB quota etc.; SW cache is still populated */
        }
      } else {
        // eslint-disable-next-line no-console
        console.warn("[offline-sync] prewarm cors fail, retrying no-cors", u, "status=", r?.status);
        failCount += 1;
        await networkFetch(u, { cache: "reload", mode: "no-cors" }).catch(() => {});
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[offline-sync] prewarm threw", u, err);
      failCount += 1;
      try {
        await networkFetch(u, { cache: "reload", mode: "no-cors" });
      } catch {
        /* both failed — skip */
      }
    }
  };
  const workers = Array.from({ length: Math.min(4, unique.length) }, async () => {
    for (;;) {
      const idx = cursor;
      cursor += 1;
      if (idx >= unique.length) return;
      await one(unique[idx]);
    }
  });
  await Promise.all(workers);
  // eslint-disable-next-line no-console
  console.info("[offline-sync] prewarm done ok=", okCount, "failed=", failCount);
};

/** Listen offline pack: cache a batch of validation clips so the
 *  Listen page keeps serving offline. Best-effort (403/empty = the
 *  page stays online-only for this project). */
const prewarmListenPack = async (projectId: number, signal?: AbortSignal): Promise<void> => {
  try {
    const r = await networkFetch(apiUrl(`/api/projects/${projectId}/validate/next/?batch=10`), {
      headers: authHeaders(),
      credentials: "include",
      signal,
    });
    if (r.ok) {
      const body = await r.json();
      const clips: Array<{ audio_url?: string | null }> = Array.isArray(body?.clips) ? body.clips : [];
      await putRecord(STORE_API_CACHE, {
        key: `validate_batch:${projectId}`,
        body,
        savedAt: Date.now(),
      });
      const clipAudio = clips.map((c) => c?.audio_url).filter(Boolean) as string[];
      if (clipAudio.length) await prewarmMedia(clipAudio, signal);
    }
  } catch (_) {
    /* Listen stays online-only for this project */
  }
};

/**
 * Pull a project's metadata + tasks + annotations + drafts into
 * IndexedDB. ``onProgress`` fires after each phase so the UI can
 * render a progress bar.
 *
 * Best-effort on per-task annotations / drafts — a single task that
 * 404s for one of those endpoints doesn't kill the whole pull.
 * The desktop sync engine uses the same forgiving semantics.
 */
export const downloadProjectForOffline = async (
  projectId: number,
  onProgress?: (p: PullProgress) => void,
  signal?: AbortSignal,
): Promise<{ tasks: number; annotations: number; drafts: number; resumedFrom: number }> => {
  const emit = (p: Omit<PullProgress, "projectId">) => onProgress?.({ projectId, ...p });

  // 1. Project metadata
  emit({ phase: "metadata", fetched: 0, total: 1 });
  const project = await fetchJson<{ id: number }>(`/api/projects/${projectId}/`, signal);
  await putRecord(STORE_PROJECTS, project);
  emit({ phase: "metadata", fetched: 1, total: 1 });

  // 1b. BULK offline pack (fast path). One paginated endpoint returns
  // tasks WITH annotations + drafts — a 166-task text project is 2
  // requests instead of ~330 ("2 minutes for text" report). When the
  // project was downloaded before, ``updated_after`` makes the pull
  // INCREMENTAL: only tasks that changed since the stamped last pull
  // come back, and ``all_ids`` prunes locally-held tasks deleted on
  // the server. Falls back to the legacy per-task path when the
  // server predates the endpoint (404).
  const LAST_PULL_KEY = `afri:offline:last-pull:${projectId}`;
  const lastPull = ((): string | null => {
    try {
      return window.localStorage.getItem(LAST_PULL_KEY);
    } catch (_) {
      return null;
    }
  })();
  try {
    type PackTask = Task & { annotations?: Annotation[]; drafts?: Draft[] };
    type Pack = {
      total: number;
      page: number;
      page_size: number;
      server_time: string;
      tasks: PackTask[];
      all_ids?: number[];
    };
    const qs = (page: number) =>
      `/api/projects/${projectId}/offline-pack/?page=${page}&page_size=100${lastPull ? `&updated_after=${encodeURIComponent(lastPull)}` : ""}`;
    const first = await networkFetch(apiUrl(qs(1)), {
      headers: { ...authHeaders(), Accept: "application/json" },
      cache: "no-store",
      signal,
    });
    if (first.status !== 404) {
      if (!first.ok) throw new Error(`offline-pack ${first.status}`);
      const firstBody = (await first.json()) as Pack;
      const expectedTotal = Number((project as { task_number?: number }).task_number ?? 0);
      if (!lastPull && firstBody.total === 0 && expectedTotal > 0) {
        throw new Error(`Download got 0 of ${expectedTotal} tasks — retry when the connection is stable`);
      }
      let annCount = 0;
      let draftCount = 0;
      let fetched = 0;
      const mediaUrls: string[] = [];
      const consume = async (body: Pack) => {
        for (const t of body.tasks) {
          bail(signal);
          const { annotations: anns = [], drafts = [], ...taskRow } = t;
          if (taskRow.data) mediaUrls.push(...extractMediaUrls(taskRow.data));
          await putRecord(STORE_TASKS, taskRow as Task);
          for (const a of anns) {
            await putRecord(STORE_ANNOTATIONS, a);
            annCount += 1;
          }
          for (const d of drafts) {
            await putRecord(STORE_DRAFTS, d);
            draftCount += 1;
          }
          fetched += 1;
          if (fetched % 10 === 0) emit({ phase: "annotations", fetched, total: body.total });
        }
      };
      emit({ phase: "tasks", fetched: 0, total: firstBody.total });
      await consume(firstBody);
      const pages = Math.ceil(firstBody.total / firstBody.page_size);
      for (let pg = 2; pg <= pages; pg += 1) {
        bail(signal);
        const r = await networkFetch(apiUrl(qs(pg)), {
          headers: { ...authHeaders(), Accept: "application/json" },
          cache: "no-store",
          signal,
        });
        if (!r.ok) throw new Error(`offline-pack page ${pg}: ${r.status}`);
        await consume((await r.json()) as Pack);
      }
      // Prune tasks deleted server-side (refresh only makes this
      // observable, but it is correct on fresh pulls too).
      if (firstBody.all_ids) {
        try {
          const live = new Set(firstBody.all_ids);
          const local = await getAllRecords<Task>(STORE_TASKS);
          for (const row of local) {
            if ((row as Task & { project?: number }).project === projectId && !live.has(row.id)) {
              await deleteRecord(STORE_TASKS, row.id).catch(() => {});
            }
          }
        } catch (_) {
          /* pruning is best-effort */
        }
      }
      emit({ phase: "annotations", fetched, total: firstBody.total });
      // Media prewarm + Listen pack (same tail as the legacy path).
      if (mediaUrls.length) await prewarmMedia(mediaUrls, signal);
      await prewarmListenPack(projectId, signal);
      try {
        window.localStorage.setItem(LAST_PULL_KEY, firstBody.server_time);
      } catch (_) {
        /* ignore */
      }
      clearCheckpoint(projectId);
      emit({ phase: "done", fetched: fetched, total: firstBody.total });
      return { tasks: fetched, annotations: annCount, drafts: draftCount, resumedFrom: 0 };
    }
  } catch (e) {
    if (e instanceof DownloadCancelled) throw e;
    // eslint-disable-next-line no-console
    console.warn("[offline-sync] offline-pack fast path failed, falling back:", e);
  }

  // 2. Tasks
  emit({ phase: "tasks", fetched: 0, total: null });
  const tasks = await iteratePaginated<Task>(`/api/projects/${projectId}/tasks/`, signal);
  // Loud failure beats a hollow success: if the cloud says the
  // project HAS tasks but the pull got none, something upstream is
  // wrong (auth blip, gateway hiccup) — do NOT mark the project
  // downloaded over an empty mirror the annotator will discover in
  // the field. ``task_number`` comes from the metadata fetch above.
  const expected = Number((project as { task_number?: number }).task_number ?? 0);
  if (tasks.length === 0 && expected > 0) {
    throw new Error(`Download got 0 of ${expected} tasks — retry when the connection is stable`);
  }
  // Diagnostic: surface the first task's data shape so we can see
  // whether the cloud is sending a usable stimulus URL. ``data`` is
  // the JSONField with task.data.audio / .csv / .image / etc; if it's
  // missing here the rest of the offline pipeline can't compensate.
  try {
    const sample = tasks[0];
    // Snapshot the first task's data + a stringified preview so the
    // device console reveals exactly which keys + values landed in
    // IDB. ``data-preview`` is truncated at 200 chars to keep the
    // log readable; full payload still goes into IDB unaltered.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sampleData = sample && (sample as any).data;
    const dataPreview = sampleData ? JSON.stringify(sampleData).slice(0, 200) : "no-data";
    // eslint-disable-next-line no-console
    console.info(
      "[offline-sync] pulled tasks",
      tasks.length,
      "first-task-keys=",
      sample ? Object.keys(sample) : "none",
      "first-data-keys=",
      sampleData && typeof sampleData === "object" ? Object.keys(sampleData) : "no-data",
      "first-data-preview=",
      dataPreview,
    );
  } catch (_) {
    /* swallow */
  }
  for (let i = 0; i < tasks.length; i += 1) {
    bail(signal);
    await putRecord(STORE_TASKS, tasks[i]);
    if (i % 10 === 0) emit({ phase: "tasks", fetched: i + 1, total: tasks.length });
  }
  emit({ phase: "tasks", fetched: tasks.length, total: tasks.length });

  // 3. Annotations + drafts per task. Best-effort — log + continue
  // if one task's endpoint errors.
  //
  // Resume: skip tasks whose ids appear in the persisted checkpoint.
  // The task's OWN metadata is re-put above (cheap; keeps STORE_TASKS
  // in sync with any cloud-side edits since last pull), but the
  // expensive per-task fetches (annotations + drafts) are skipped so
  // a locked-mid-download phone doesn't lose the 240 tasks it
  // already got through. Prewarm URLs are still collected for every
  // task because the shared-media dedup happens at prewarm time.
  const completed = readCheckpoint(projectId);
  const skippedCount = completed.size;
  let annCount = 0;
  let draftCount = 0;
  const allMediaUrls: string[] = [];
  // Media URLs + skip bookkeeping first (sync, cheap).
  const pending: Task[] = [];
  for (const t of tasks) {
    if (t.data) allMediaUrls.push(...extractMediaUrls(t.data));
    if (!completed.has(t.id)) pending.push(t);
  }
  // Per-task annotations + drafts with a SMALL CONCURRENCY POOL.
  // Sequentially this was 2 round trips × N tasks — 166 tasks ≈ 330
  // serial requests, minutes on a phone network for kilobytes of
  // data ("annotations+drafts taking time"). Six in flight cuts the
  // wall clock ~6× while staying gentle on the server. Checkpoint
  // semantics unchanged: a task is marked complete only after BOTH
  // fetches landed; writes stay throttled.
  let done = 0;
  let cursor = 0;
  const CONCURRENCY = 6;
  const worker = async (): Promise<void> => {
    for (;;) {
      bail(signal);
      const idx = cursor;
      cursor += 1;
      if (idx >= pending.length) return;
      const t = pending[idx];
      try {
        const anns = await iteratePaginated<Annotation>(`/api/tasks/${t.id}/annotations`, signal);
        for (const a of anns) await putRecord(STORE_ANNOTATIONS, a);
        annCount += anns.length;
      } catch (e) {
        if (e instanceof DownloadCancelled) throw e;
        /* skip — same forgiving semantics as desktop */
      }
      try {
        const drafts = await iteratePaginated<Draft>(`/api/tasks/${t.id}/drafts`, signal);
        for (const d of drafts) await putRecord(STORE_DRAFTS, d);
        draftCount += drafts.length;
      } catch (e) {
        if (e instanceof DownloadCancelled) throw e;
        /* skip */
      }
      completed.add(t.id);
      if (completed.size % 5 === 0) writeCheckpoint(projectId, completed);
      done += 1;
      if (done % 10 === 0) emit({ phase: "annotations", fetched: skippedCount + done, total: tasks.length });
    }
  };
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, pending.length) }, () => worker()));
  // Final checkpoint flush so the last <5 tasks aren't lost on cancel
  // between the loop and the media prewarm.
  writeCheckpoint(projectId, completed);

  // 4. Pre-warm the SW media cache. ``cache: 'reload', mode:
  // 'no-cors'`` so the SW captures cross-origin S3/presign URLs
  // even without CORS headers. Best-effort throughout.
  if (allMediaUrls.length) {
    emit({ phase: "annotations", fetched: tasks.length, total: tasks.length });
    await prewarmMedia(allMediaUrls, signal);
  }
  await prewarmListenPack(projectId, signal);

  emit({ phase: "done", fetched: tasks.length, total: tasks.length });

  // Successful completion — clear the checkpoint so a subsequent
  // "Re-download" starts fresh instead of thinking every task is
  // already done.
  clearCheckpoint(projectId);

  return {
    tasks: tasks.length,
    annotations: annCount,
    drafts: draftCount,
    resumedFrom: skippedCount,
  };
};
